<template>
  <div class="flex-1 overflow-auto p-4">
    <form class="mx-auto flex w-full max-w-full flex-col" @submit.prevent="updateSpamFilter">
      <div class="w-full">
        <div class="mb-4">
          <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
            {{ $t('SPAM_FILTER.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {{ $t('SPAM_FILTER.DESCRIPTION') }}
          </p>
        </div>

        <div class="mb-6">
          <label class="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ $t('SPAM_FILTER.KEYWORDS_LABEL') }}
          </label>
          <textarea
            v-model="spamKeywords"
            rows="6"
            class="w-full rounded-md border border-slate-300 p-3 text-sm focus:border-woot-500 focus:ring-1 focus:ring-woot-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            :placeholder="$t('SPAM_FILTER.KEYWORDS_PLACEHOLDER')"
          />
          <div class="mt-2 flex items-center justify-between">
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('SPAM_FILTER.KEYWORDS_HELP') }}
            </p>
            <p class="text-xs font-medium text-slate-700 dark:text-slate-300">
              {{ keywordCount }} {{ $t('SPAM_FILTER.KEYWORDS_COUNT') }}
            </p>
          </div>
        </div>

        <div class="mb-6 rounded-md bg-yellow-50 p-4 dark:bg-yellow-900/20">
          <div class="flex">
            <div class="flex-shrink-0">
              <fluent-icon
                icon="warning"
                size="20"
                class="text-yellow-600 dark:text-yellow-400"
              />
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {{ $t('SPAM_FILTER.HOW_IT_WORKS_TITLE') }}
              </h3>
              <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <ul class="list-disc space-y-1 pl-5">
                  <li>{{ $t('SPAM_FILTER.HOW_IT_WORKS_1') }}</li>
                  <li>{{ $t('SPAM_FILTER.HOW_IT_WORKS_2') }}</li>
                  <li>{{ $t('SPAM_FILTER.HOW_IT_WORKS_3') }}</li>
                  <li>{{ $t('SPAM_FILTER.HOW_IT_WORKS_4') }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="flex gap-2">
          <NextButton
            type="submit"
            :is-loading="isUpdating"
            :disabled="isUpdating"
            blue
          >
            {{ $t('SPAM_FILTER.SAVE_BUTTON') }}
          </NextButton>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SpamFilterAPI from '../../../../api/spamFilter';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const spamKeywords = ref('');
const isUpdating = ref(false);
const isLoading = ref(false);

const keywordCount = computed(() => {
  if (!spamKeywords.value || spamKeywords.value.trim() === '') return 0;
  return spamKeywords.value.split(',').map(k => k.trim()).filter(k => k !== '').length;
});

const fetchSpamFilter = async () => {
  isLoading.value = true;
  try {
    const response = await SpamFilterAPI.get();
    spamKeywords.value = response.data.spam_keywords || '';
  } catch (error) {
    useAlert(t('SPAM_FILTER.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const updateSpamFilter = async () => {
  isUpdating.value = true;
  try {
    await SpamFilterAPI.update(spamKeywords.value);
    useAlert(t('SPAM_FILTER.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error || t('SPAM_FILTER.API.ERROR');
    useAlert(errorMessage);
  } finally {
    isUpdating.value = false;
  }
};

onMounted(() => {
  fetchSpamFilter();
});
</script>
